/**
 * Vendor stats queries for TanStack Query (Vendor App)
 */

import { useQuery } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet } from '../utils/request'

// Type definitions
export interface CompanyStats {
  tenants: {
    total: number
  }
  companies: {
    total: number
  }
}

export interface UserStats {
  total: number
}

export interface CouponStats {
  total: number
}

export interface TotalCompaniesParams {
  date_start?: string
  date_end?: string
  grouping?: 'daily' | 'weekly' | 'monthly'
  [key: string]: any
}

export interface TotalCompaniesData {
  // This will contain the chart data from the API
  [key: string]: any
}

// Cache configuration
const CACHE_TIMES = {
  STATS: 2 * 60 * 1000, // 2 minutes
  TOTALS: 5 * 60 * 1000, // 5 minutes
}

/**
 * QUERIES
 */

/**
 * Query hook to fetch company stats (tenants and companies count)
 */
export const useVendorCompanyStatsQuery = () => {
  return useQuery({
    queryKey: [...queryKeys.vendorStats(), 'companies'],
    queryFn: async (): Promise<CompanyStats> => {
      return queryGet<CompanyStats>('/v1/vendor/stats/companies/')
    },
    staleTime: CACHE_TIMES.STATS,
  })
}

/**
 * Query hook to fetch user stats
 */
export const useVendorUserStatsQuery = () => {
  return useQuery({
    queryKey: [...queryKeys.vendorStats(), 'users'],
    queryFn: async (): Promise<UserStats> => {
      return queryGet<UserStats>('/v1/vendor/stats/users/')
    },
    staleTime: CACHE_TIMES.STATS,
  })
}

/**
 * Query hook to fetch coupon stats
 */
export const useVendorCouponStatsQuery = () => {
  return useQuery({
    queryKey: [...queryKeys.vendorStats(), 'coupons'],
    queryFn: async (): Promise<CouponStats> => {
      return queryGet<CouponStats>('/v1/vendor/stats/coupons/')
    },
    staleTime: CACHE_TIMES.STATS,
  })
}

/**
 * Query hook to fetch total companies data for charts
 */
export const useVendorTotalCompaniesQuery = (params: MaybeRefOrGetter<TotalCompaniesParams> = {}) => {
  return useQuery({
    queryKey: computed(() => [...queryKeys.vendorStats(), 'totals', toValue(params)]),
    queryFn: async (): Promise<TotalCompaniesData> => {
      const queryParams = toValue(params)
      return queryGet<TotalCompaniesData>('/v1/vendor/stats/companies/totals', { params: queryParams })
    },
    staleTime: CACHE_TIMES.TOTALS,
    enabled: computed(() => {
      const p = toValue(params)
      return !!(p.date_start && p.date_end)
    }),
  })
}
